/* eslint-disable react/jsx-pascal-case */
import { Formik } from "formik";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Translation as T } from "@awe-web/shared/lib";
import { Button } from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";
import { ScreenLoading, ScreenSection } from "../../../components/screen.js";
import { B2B_STR_SelectRentObject } from "../../../routing/routes.js";
import { useInitialize } from "../../../util/react.js";

import STR_SelectExtrasForm from "../../../rent_contracts/components/str_select_extras_form.jsx";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";

import {
  getChangedEquipmentOptionsMessage,
  getChangedStrMileageOptionsMessage,
  getStrDeductibleOptions,
  getStrSelectedEquipmentOptions,
  getStrSelectedMileageOptions
} from "../../../rent_contracts/store.js";
import { initialize, submit } from "./actions.js";

import "./select_extras.scss";

const BACK = "ALG0000030";
const PRICES_NOTICE = "ALG0000029";
const EXCLUDING_VAT = "ALG0000245";

function InternalSelectExtrasForm({ onSubmit }) {
  const deductibleOptions = useSelector((state) => getStrDeductibleOptions(state));
  const selectedMileageOptions = useSelector((state) => getStrSelectedMileageOptions(state));
  const selectedEquipmentOptions = useSelector((state) => getStrSelectedEquipmentOptions(state));
  const mileageOptionsChangedMessage = useSelector((state) => getChangedStrMileageOptionsMessage(state));
  const equipmentOptionsChangedMessage = useSelector((state) => getChangedEquipmentOptionsMessage(state));

  const enabledDeductible = useMemo(() => {
    return deductibleOptions?.toJS().find(({ enabled }) => enabled);
  }, [deductibleOptions]);

  const initialValues = {
    mileage: selectedMileageOptions.toJS(),
    deductible: enabledDeductible && enabledDeductible.id,
    equipment: selectedEquipmentOptions.toJS()
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <STR_SelectExtrasForm
          {...formikProps}
          reservationPath="B2B"
          basePriceHintCode={EXCLUDING_VAT}
          deductibleOptions={deductibleOptions.toJS()}
          mileageOptionsChangedMessage={mileageOptionsChangedMessage}
          equipmentOptionsChangedMessage={equipmentOptionsChangedMessage}
        />
      )}
    </Formik>
  );
}

export default function B2B_STR_SelectExtras() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (values) => {
    const nextPath = await dispatch(submit(values));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(async () => {
    const redirectPath = await dispatch(initialize());

    if (redirectPath && redirectPath.length > 0) {
      navigate(redirectPath);
    }
  }, [dispatch, navigate]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2b-str-select-extras">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2B" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2b-str-select-extras__selected-rent-object"
            selectedBasicProperties="B2B"
            action={(
              <Button secondary back to={B2B_STR_SelectRentObject.path()}>
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <InternalSelectExtrasForm onSubmit={onSubmit} />

      <ScreenSection className="b2b-str-select-extras__prices-notice">
        <TypeArea>
          <Row>
            <Col desktop={{ offset: 1 }}>
              <T code={PRICES_NOTICE} />
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>
    </div>
  );
}
